<template>
  <section class="hero-section pt-24 pb-16 text-white" :class="backgroundClass" :style="sectionStyle">
    <div class="grid gap-10 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-2">
      <div>
        <h1
          class="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight min-h-[7.25rem] sm:min-h-[8.75rem] lg:min-h-[11.5rem] text-shadow-hero"
          :class="titleClass">
          {{ title }}
        </h1>
        <p class="mt-4 text-base sm:text-lg text-slate-200 min-h-[3rem] sm:min-h-[3.5rem] text-shadow-hero">
          {{ description }}
        </p>
        <div class="flex flex-col gap-4 mt-8 sm:flex-row">
          <a v-if="primaryLabel && !primaryOpensDemo" :href="primaryHref" target="_blank" rel="noopener noreferrer"
            class="inline-flex justify-center items-center px-6 py-3 text-base font-semibold text-white rounded-md border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600">
            {{ primaryLabel }}
          </a>
          <button v-else-if="primaryLabel" type="button" @click="openScheduleDemo"
            class="inline-flex justify-center items-center px-6 py-3 text-base font-semibold text-white rounded-md border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600">
            {{ primaryLabel }}
          </button>
          <a v-if="secondaryHref && secondaryLabel" :href="secondaryHref"
            class="inline-flex justify-center items-center px-6 py-3 text-base font-medium rounded-md border border-slate-500 text-slate-100 hover:bg-slate-800">
            {{ secondaryLabel }}
          </a>
        </div>
        <p v-if="disclaimer" class="mt-3 text-xs text-slate-300">
          {{ disclaimer }}
        </p>
      </div>
      <div v-if="$slots.default" class="relative">
        <slot />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";

defineOptions({ name: "SegmentHero" });

const {
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  disclaimer,
  backgroundImage,
  backgroundClass,
  overlay,
  overlayVariant,
  titleClass,
  primaryOpensDemo,
} = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  primaryLabel: {
    type: String,
    default: "Solicite uma demonstração gratuita",
  },
  primaryHref: {
    type: String,
    default: "/contato",
  },
  secondaryLabel: {
    type: String,
    default: "",
  },
  secondaryHref: {
    type: String,
    default: "",
  },
  disclaimer: {
    type: String,
    default: "",
  },
  backgroundImage: {
    type: String,
    default: "",
  },
  backgroundClass: {
    type: String,
    default: "bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800",
  },
  overlay: {
    type: Boolean,
    default: true,
  },
  overlayVariant: {
    type: String,
    default: "vertical",
  },
  titleClass: {
    type: String,
    default: "",
  },
  primaryOpensDemo: {
    type: Boolean,
    default: true,
  },
});

const sectionStyle = computed(() => {
  if (!backgroundImage) return null;

  if (!overlay) {
    return {
      backgroundImage: `url(${backgroundImage})`,
    };
  }

  const gradient =
    overlayVariant === "left"
      ? "linear-gradient(to right, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.74) 35%, rgba(15,23,42,0.35) 60%, rgba(15,23,42,0) 82%)"
      : "linear-gradient(to bottom, rgba(15,23,42,0.6), rgba(15,23,42,0.75))";

  return {
    backgroundImage: `${gradient}, url(${backgroundImage})`,
  };
});

const openScheduleDemo = () => {
  window.dispatchEvent(new CustomEvent("open-demo-modal"));
};
</script>

<style scoped>
.text-shadow-hero {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}
</style>
